import { parseBridgeCallTarget } from './call-target-parser';

type HostBridgeIncomingAction = 'call' | 'drop';
type HostBridgeOutgoingAction = 'logged-in' | 'called' | 'closed';
type HostBridgeOutgoingMessage = {
	action: HostBridgeOutgoingAction;
	param?: Record<string, unknown>;
};

type HostBridgeRawPayload = {
	action?: unknown;
	param?: {
		peer?: unknown;
	};
};

type HostBridgeCallCommand = {
	action: 'call';
	peer: string;
	target: string;
};

type HostBridgeIncomingEvent =
	| {
			action: Exclude<HostBridgeIncomingAction, 'call'>;
	  }
	| HostBridgeCallCommand;

type HostBridgeSubscriber = (event: HostBridgeIncomingEvent) => void;

const isAction = (value: unknown): value is HostBridgeIncomingAction =>
	typeof value === 'string' && ['call', 'drop'].includes(value);

/**
 * Нормализует входящий peer из host-приложения.
 *
 * Правила:
 * - `sip:*` принимается как есть (с валидацией);
 * - телефон принимается в формате `tel:+7...`;
 * - plain-номер `+7...` без схемы тоже поддерживается.
 */
const normalizeHostBridgePeer = (rawPeer: string): string | null => {
	const peer = rawPeer.trim();
	if (!peer) return null;

	try {
		return parseBridgeCallTarget(peer);
	} catch {
		return null;
	}
};

const resolveHostWindow = (): Window | null => {
	const openerWindow = window.opener;
	if (openerWindow && !openerWindow.closed) {
		return openerWindow;
	}

	if (window.parent && window.parent !== window) {
		return window.parent;
	}

	return null;
};

/**
 * Проверяет, доступен ли host-контекст для bridge-коммуникации.
 * В v1 поддерживаются два режима:
 * - popup (`window.opener`)
 * - iframe (`window.parent`)
 */
export const hasHostBridgeContext = (): boolean => Boolean(resolveHostWindow());

/**
 * Адаптер обмена сообщениями между host-приложением и S2-клиентом.
 * Принимает входящие bridge-команды и отправляет lifecycle-события звонка.
 */
class HostBridgeAdapter {
	private subscribers = new Set<HostBridgeSubscriber>();
	private isListening = false;
	private readonly targetOrigin = '*';

	private readonly messageHandler = (event: MessageEvent<HostBridgeRawPayload>) => {
		const eventData = event.data;
		if (!eventData || typeof eventData !== 'object') return;
		if (!isAction(eventData.action)) return;

		switch (eventData.action) {
			case 'drop': {
				this.emit({ action: eventData.action });
				return;
			}
			case 'call': {
				const rawPeer = eventData.param?.peer;
				if (typeof rawPeer !== 'string') return;

				const normalizedTarget = normalizeHostBridgePeer(rawPeer);
				if (!normalizedTarget) return;

				this.emit({
					action: 'call',
					peer: rawPeer,
					target: normalizedTarget,
				});
				break;
			}
			default: {
				break;
			}
		}
	};

	/**
	 * Подписывает обработчик на входящие bridge-события (`call`/`drop`).
	 * Возвращает функцию отписки.
	 */
	public subscribe(handler: HostBridgeSubscriber) {
		this.subscribers.add(handler);
		this.ensureListener();

		return () => {
			this.subscribers.delete(handler);
			this.cleanupListener();
		};
	}

	/**
	 * Отправляет bridge-событие в host-приложение через `postMessage`.
	 * Если host-контекст недоступен, событие тихо игнорируется.
	 */
	public send(event: HostBridgeOutgoingMessage): void {
		const hostWindow = resolveHostWindow();
		if (!hostWindow) {
			return;
		}

		// TODO: включить strict-проверку origin по allowlist в следующем релизе.
		hostWindow.postMessage(event, this.targetOrigin);
	}

	private emit(event: HostBridgeIncomingEvent) {
		this.subscribers.forEach((handler) => {
			handler(event);
		});
	}

	private ensureListener() {
		if (this.isListening) return;
		window.addEventListener('message', this.messageHandler);
		this.isListening = true;
	}

	private cleanupListener() {
		if (this.subscribers.size > 0 || !this.isListening) return;
		window.removeEventListener('message', this.messageHandler);
		this.isListening = false;
	}
}

/** Singleton-экземпляр bridge-адаптера для использования в feature call. */
export const hostBridgeAdapter = new HostBridgeAdapter();
export { normalizeHostBridgePeer };
